"use client";

import { useContext, useEffect } from "react";
import dynamic from "next/dynamic";
import { useWindowSize } from "@/hooks/browser/useWindowsSize";
import { useIntersectionObserver } from "@/hooks/observers/useIntersectionObserver";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { BannerWrapperSize } from "@/components/Banner/types";
import { I_HomePage } from "@/data/types/pages";
import { GridModuleSkeleton } from "@/components/GridModule";
import { QREditorSkeletonWrapper } from "@/components/QREditorSkeleton";

import "@/components/home-legacy/styles.scss";

const QREditorHomeModule = dynamic(
  () =>
    import("@/components/QREditorHomeModule").then((mod) => ({
      default: mod.QREditorHomeModule,
    })),
  {
    ssr: false,
    loading: () => <QREditorSkeletonWrapper />,
  }
);

const HeroModuleLegacy = dynamic(
  () => import("@/components/home-legacy").then((mod) => ({ default: mod.HeroModuleLegacy })),
  { ssr: false }
);

const ThreeStepsModuleLegacy = dynamic(
  () =>
    import("@/components/home-legacy").then((mod) => ({
      default: mod.ThreeStepsModuleLegacy,
    })),
  { ssr: false }
);

const GridModuleLegacy = dynamic(
  () =>
    import("@/components/home-legacy").then((mod) => ({
      default: mod.GridModuleLegacy,
    })),
  { ssr: false, loading: () => <GridModuleSkeleton /> }
);

const AIModuleLegacy = dynamic(
  () => import("@/components/home-legacy").then((mod) => ({ default: mod.AIModuleLegacy })),
  { ssr: false }
);

const KeepItSimpleModuleLegacy = dynamic(
  () =>
    import("@/components/home-legacy").then((mod) => ({
      default: mod.KeepItSimpleModuleLegacy,
    })),
  { ssr: false }
);

const FaqsModuleLegacy = dynamic(
  () => import("@/components/home-legacy").then((mod) => ({ default: mod.FaqsModuleLegacy })),
  { ssr: false }
);

const MoreModule = dynamic(
  () => import("@/components/MoreModule").then(({ MoreModule }) => MoreModule),
  {
    ssr: false,
  }
);

export const LegacyHomePageTemplate = ({
  data,
  amplitudeEvent,
}: {
  data: I_HomePage;
  amplitudeEvent?: string;
}) => {
  const screenSize = useWindowSize();
  const { setQrSelected } = useContext(AmplitudeContext);
  const { ref: gridModuleRef, isIntersecting: shouldLoadGridModule } =
    useIntersectionObserver<HTMLDivElement>({
      rootMargin: screenSize?.isTablet ? "-100px" : "200px",
      triggerOnce: true,
    });

  const getWrapperSize = (isTablet: BannerWrapperSize, isNotTablet: BannerWrapperSize) => {
    return screenSize?.isTablet ? isTablet : isNotTablet;
  };

  useEffect(() => {
    setQrSelected(undefined);
    amplitudeEvent && amplitudeTrackEvent(amplitudeEvent);
  }, []);

  return (
    <main data-testid="legacy-home-page">
      {data.qrEditorCompact && <QREditorHomeModule data={data.qrEditorCompact} />}

      {data.billboard && (
        <HeroModuleLegacy
          data={data.billboard}
          wrapperSize={getWrapperSize("sm", "lg")}
        />
      )}

      <ThreeStepsModuleLegacy
        data={data.featured2}
        wrapperSize={getWrapperSize("md", "lg")}
      />

      <AIModuleLegacy data={data.featuredAI} />

      <div ref={gridModuleRef}>
        {shouldLoadGridModule && (
          <GridModuleLegacy
            data={data.previewHome}
            wrapperSize={getWrapperSize("xs", "lg")}
            previewTranslations={data.previewTranslations}
            defaultPreviewTitle={data.previewHome.defaultPreviewTitle}
          />
        )}
      </div>

      <KeepItSimpleModuleLegacy
        data={data.banner}
        wrapperSize={getWrapperSize("sm", "lg")}
      />

      <FaqsModuleLegacy
        data={{
          title: data.faqsHome.title,
          faqsHomeTabs: data.faqsHomeTabs,
        }}
        wrapperSize="md"
        backgroundColor={data.faqsHome.backgroundColor}
        customClass={data.faqsHome.customClass}
      />

      {data.banner2 && (
        <MoreModule
          data={data.banner2}
          wrapperSize="md"
        />
      )}
    </main>
  );
};
